/**
 * AAL (Authenticator Assurance Level) step-up gate hook.
 *
 * Source of truth for "is this AAL1 session allowed to perform a sensitive
 * action, or must it step up to AAL2 first?" decisions on the client.
 *
 * Resolves SEC-AUTH-04 (Plan A.1 Tier 1 / RM-T1-14, beta blocker). With MFA
 * now enforced at the dashboard level (Phase 110+), an attacker who hijacks an
 * AAL1 password-only session must not be able to silently change the password
 * and lock the legitimate user out. Sensitive surfaces (currently:
 * change-password; future: API key rotation, account deletion, email change —
 * tracked as RM-T2-22) must gate on `useRequireAAL2()` before exposing the
 * action UI.
 *
 * State machine — 5 values, all surfaced via React Query for reactivity:
 *
 *   loading              — initial AAL fetch in flight. UI should render a
 *                          spinner; do NOT show the form yet (would invite a
 *                          race where the submit lands before AAL resolves).
 *   not-enrolled         — Supabase POSITIVELY established the account has no
 *                          verified factor. Pass through with no step-up: AAL2
 *                          is only required of users who CAN reach it, and
 *                          gating a no-factor account is unsatisfiable.
 *   aal1-needs-step-up   — user has a verified factor but the current session
 *                          is AAL1. Block the action and render an MFA
 *                          challenge UI; on successful verify, the hook
 *                          re-evaluates and unblocks.
 *   aal2-satisfied       — current session is already AAL2. Pass through.
 *   unknown              — the account's MFA position could not be established.
 *                          Fails closed to the retry screen.
 *
 * FAIL-CLOSED (SEC-AUTH-02e):
 *   `not-enrolled` is a POSITIVE finding and nothing else. It requires BOTH
 *   probes to have succeeded and to agree there is no factor.
 *
 *   The earlier version returned `not-enrolled` when both calls FAILED —
 *   reasoning that with no factorId it could not present a challenge, so
 *   passing through avoided trapping the user. That reasoning is wrong once
 *   this hook also gates route groups: it means anyone who can make those two
 *   calls fail turns the gate off. The escape from being trapped is the retry +
 *   sign-out screen, not admission.
 *
 * Why a hook (not a synchronous boolean):
 *   The gate is async (network round-trip) AND reactive (user may complete the
 *   MFA challenge while on the same screen). Exposing a sync check would
 *   invite race conditions where the form submits before AAL state resolves.
 *
 * IMPORTANT: This hook does NOT import `@supabase/supabase-js` directly. It
 * uses the re-exported `supabase` client from `@/lib/supabase` so test mocks
 * and singleton invariants are preserved.
 */

import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import type { AALGateStatus } from './guard-state';

export type AALState =
  | { status: 'loading' }
  | { status: 'not-enrolled' }
  | { status: 'aal1-needs-step-up'; factorId: string }
  | { status: 'aal2-satisfied' }
  /**
   * We could not establish the account's MFA position (SEC-AUTH-02e).
   *
   * DISTINCT FROM `not-enrolled` ON PURPOSE. The previous code collapsed the
   * two, so a failure to look up enrollment was indistinguishable from a
   * confirmed absence of enrollment — and the route guard let the session
   * through. Anyone who could make the two MFA calls fail could therefore
   * switch the client gate off. `not-enrolled` is now a POSITIVE finding only;
   * everything else lands here and fails closed to a retryable screen.
   */
  | { status: 'unknown' };

type ResolvedAALState = Exclude<AALState, { status: 'loading' }>;

/**
 * The raw facts gathered from Supabase, separated from the decision so the
 * decision can be exhaustively unit-tested without a network or a React tree.
 */
export interface AALProbe {
  /** Did `getAuthenticatorAssuranceLevel()` succeed? */
  aalOk: boolean;
  currentLevel?: string;
  nextLevel?: string;
  /** Did `listFactors()` succeed? */
  factorsOk: boolean;
  /** First VERIFIED factor id, when one was positively retrieved. */
  verifiedFactorId?: string;
}

/**
 * Pure AAL decision. Fail-closed by construction: every path that does not
 * positively establish the account's position returns `unknown`.
 */
export function resolveAALState(probe: AALProbe): ResolvedAALState {
  const { aalOk, currentLevel, nextLevel, factorsOk, verifiedFactorId } = probe;

  // Already stepped up. The session itself is the proof — no factor lookup is
  // needed, so an unrelated listFactors failure must not drag this to unknown.
  if (aalOk && currentLevel === 'aal2') return { status: 'aal2-satisfied' };

  // A verified factor in hand: we know they are enrolled and not stepped up.
  // This holds even when the AAL probe failed — enrollment is the fact that
  // matters, and we have a factorId to challenge with.
  if (verifiedFactorId) {
    return { status: 'aal1-needs-step-up', factorId: verifiedFactorId };
  }

  // From here on we have no factor id, so anything but a POSITIVE "no factor
  // exists" is unknown.
  if (!aalOk || !factorsOk) return { status: 'unknown' };

  // Both probes succeeded. `nextLevel === 'aal1'` is Supabase positively
  // stating the account cannot reach aal2 — i.e. no verified factor.
  if (nextLevel === 'aal1') return { status: 'not-enrolled' };

  // Both succeeded but Supabase says the account CAN reach aal2 while returning
  // no verified factor. That is internally inconsistent: aal2 is reachable only
  // when a factor exists. Reading it as not-enrolled would wave through an
  // account we were just told is enrolled.
  return { status: 'unknown' };
}

/**
 * React Query key for the AAL probe.
 *
 * Exported because several places need to invalidate or refetch it — the route
 * guards' Retry action, and mfa-verify after a successful step-up. Hard-coding
 * the string at each site is how they drift apart, and a missed invalidation
 * here is not cosmetic: it leaves a stale `unknown` in place, which the guard
 * reads as `error` forever.
 */
export const AAL_QUERY_KEY = ['auth-aal2-state'] as const;

/**
 * Reactive AAL2 enforcement hook.
 *
 * Calls `supabase.auth.mfa.getAuthenticatorAssuranceLevel()` and (when needed)
 * `supabase.auth.mfa.listFactors()` to determine whether the current session
 * satisfies AAL2 or must step up. Returns one of the 5 `AALState` values.
 *
 * Demo / unconfigured mode: when Supabase is not configured (mock mode), the
 * hook short-circuits to `aal2-satisfied` — there is no real session to step
 * up, and the gated UI must remain reachable for design/QA work.
 */
export function useRequireAAL2(): AALState {
  const supabaseConfigured = isSupabaseConfigured();

  const { data, isLoading } = useQuery<ResolvedAALState>({
    queryKey: AAL_QUERY_KEY,
    // Always fresh enough — the user can change AAL during the screen lifetime
    // by completing a challenge, so we want a manual refetch path more than
    // long staleness.
    staleTime: 0,
    enabled: supabaseConfigured,
    queryFn: async (): Promise<ResolvedAALState> => {
      // Step 1: resolve AAL.
      let currentLevel: string | undefined;
      let nextLevel: string | undefined;
      let aalFailed = false;

      try {
        const { data: aalData, error: aalError } =
          await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (aalError) throw aalError;
        currentLevel = aalData?.currentLevel ?? undefined;
        nextLevel = aalData?.nextLevel ?? undefined;
      } catch {
        aalFailed = true;
      }

      // Fast path: already AAL2 — the session is its own proof, so skip the
      // factor lookup entirely.
      if (!aalFailed && currentLevel === 'aal2') {
        return resolveAALState({ aalOk: true, currentLevel, nextLevel, factorsOk: true });
      }

      // NOTE: there is deliberately NO 'nextLevel === aal1 → not-enrolled'
      // fast path here. That shortcut skipped listFactors, so a later
      // failure could not be distinguished from a genuine absence of
      // factors. resolveAALState requires BOTH probes to have succeeded
      // before it will say not-enrolled.

      // Step 2: we need a factorId — either to present the challenge
      // (aal1 → aal2 path) or to distinguish "no factor" from "could not ask".
      let firstVerifiedFactorId: string | undefined;
      let factorsFailed = false;

      try {
        const { data: factorsData, error: factorsError } =
          await supabase.auth.mfa.listFactors();
        if (factorsError) throw factorsError;
        const verified = factorsData?.totp?.filter((f) => f.status === 'verified') ?? [];
        firstVerifiedFactorId = verified[0]?.id;
      } catch {
        factorsFailed = true;
      }

      // The DECISION lives in resolveAALState (pure, exhaustively unit-tested
      // in __tests__/aal-resolve.test.ts). Everything above only gathers facts.
      // Keeping them apart is what makes "we could not find out" impossible to
      // confuse with "there is no factor" — the bug this replaced.
      return resolveAALState({
        aalOk: !aalFailed,
        currentLevel,
        nextLevel,
        factorsOk: !factorsFailed,
        verifiedFactorId: firstVerifiedFactorId,
      });
    },
  });

  if (!supabaseConfigured) {
    // Mock mode — preserve UX, gated UI remains reachable.
    return { status: 'aal2-satisfied' };
  }

  if (isLoading || !data) {
    return { status: 'loading' };
  }

  return data;
}

/**
 * Map the 4-state `AALState` onto the coarser decision the ROUTE GUARDS need.
 *
 * Pure and exported so the mapping is unit-testable, and so the guards and the
 * sensitive-action screens provably share one definition of "is this session
 * strong enough" rather than each re-deriving a subtly different one.
 *
 * 'not-enrolled' maps to 'satisfied' ON PURPOSE. MFA is optional here (product
 * policy — see the module header), and a user with no verified factor can never
 * reach aal2, so requiring it would be an unsatisfiable gate that locks every
 * non-MFA user out of the entire app. The gate constrains users who CAN step
 * up and have not.
 */
export function toAALGateStatus(state: AALState): AALGateStatus {
  switch (state.status) {
    case 'loading':
      return 'loading';
    case 'aal1-needs-step-up':
      return 'step-up-required';
    case 'unknown':
      // FAIL CLOSED. Routes to the existing AuthGuardError retry screen, which
      // also offers a sign-out escape hatch — so a user whose MFA status cannot
      // be established is never silently admitted, and never trapped either.
      return 'error';
    case 'aal2-satisfied':
    case 'not-enrolled':
      return 'satisfied';
  }
}

/**
 * Route-group AAL gate (SEC-AUTH-02c).
 *
 * THE HOLE THIS CLOSES: `signInWithPassword` mints a real, persisted AAL1
 * session BEFORE MFA is verified. The group layouts checked user + role and
 * nothing else, so an enrolled user who abandoned the /mfa-verify screen — or
 * who deep-linked past it — was inside the app, holding a token that PostgREST
 * and the edge functions both accept. The MFA screen was presentation, not a
 * boundary.
 *
 * Feed the result into `resolveGuardState({ ..., aal })`. This is defence in
 * depth on the client; the enforceable boundaries are the RESTRICTIVE RLS
 * policies (migration 20260806110000) and `requireAAL2` in the edge functions.
 */
export function useAALGate(): { status: AALGateStatus; retry: () => void } {
  const state = useRequireAAL2();
  const queryClient = useQueryClient();

  // The AAL probe lives in its OWN query, separate from the role fetch. The
  // guards' Retry used to call only `refetchRole()`, so an AAL failure — the
  // very thing that put the user on the error screen — was never re-attempted
  // and the screen could not recover. Retry must reset THIS query.
  const retry = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: AAL_QUERY_KEY });
  }, [queryClient]);

  return { status: toAALGateStatus(state), retry };
}
